import {
  addDays,
  addMonths,
  differenceInDays,
  differenceInMonths,
  eachDayOfInterval,
  eachWeekOfInterval,
  endOfISOWeek,
  endOfMonth,
  endOfWeek,
  format,
  formatDuration,
  getISOWeek,
  getISOWeekYear,
  getISOWeeksInYear,
  isSameDay,
  isValid,
  isWeekend,
  parseISO,
  setISOWeek,
  startOfDay,
  startOfISOWeek,
  startOfMonth,
  startOfWeek,
  subDays,
} from "date-fns";
import { fr } from "date-fns/locale";
import type { Month } from "./supabase.enum";
import { numberToMonthMap } from "../types/date";
import { PAY_BLOCK_DATE } from "../constants/advance-payment";
import { useHolidays } from "./holidays";

type DateFormatParams = {
  locales?: string | string[] | undefined;
  options?: Intl.DateTimeFormatOptions;
};

export const dateFormat = function (date: string | number | Date, params?: DateFormatParams) {
  const { locales = "fr" } = params ?? {};
  const { options = { dateStyle: "full" } } = params ?? {};
  return new Intl.DateTimeFormat(locales, options).format(new Date(date));
};

export const dateFormatWithTime = function (date: string | number | Date, params?: DateFormatParams) {
  const { locales = "fr" } = params ?? {};
  const { options = { dateStyle: "short", timeStyle: "short" } } = params ?? {};

  return new Intl.DateTimeFormat(locales, options).format(new Date(date));
};

export const durationTime = function ({
  start,
  end,
}: {
  start: string | number | Date | undefined;
  end: string | number | Date | undefined;
}) {
  if (!start) return undefined;
  if (!end) return undefined;

  const diffInMonths = differenceInMonths(addDays(new Date(end), 1), new Date(start));
  const diffInDays = differenceInDays(addDays(new Date(end), 1), addMonths(new Date(start), diffInMonths));

  return { months: diffInMonths, days: diffInDays };
};

export const durationFormat = function ({
  start,
  end,
}: {
  start: string | number | Date | undefined;
  end: string | number | Date | undefined;
}) {
  const duration = durationTime({ start, end });

  if (!duration) return undefined;

  return formatDuration({ months: duration.months, days: duration.days }, { locale: fr, delimiter: " et " });
};

export const startOfFrenchMonth = function (date: string | number | Date) {
  const currentDate = new Date(date);
  currentDate.setDate(1);
  currentDate.setHours(0, 0, 0, 0);
  return currentDate;
};

export const endOfFrenchMonth = function (date: string | number | Date) {
  const currentDate = startOfFrenchMonth(date);
  currentDate.setMonth(currentDate.getMonth() + 1);
  currentDate.setDate(currentDate.getDate() - 1);
  currentDate.setHours(23, 59, 59, 999);
  return currentDate;
};

export const getDateOnlyFromIsoDateTime = (isoDateTime: string): string => isoDateTime.split("T")[0];

// Whatever the time zone of the machine, unlike parseISO which gives local midnight
export const getUtcMidnightFromIsoDate = (isoDate: string): Date => new Date(`${isoDate}T00:00:00.000Z`);

export const getMinSelectableDateFromIsoDateTime = (isoDateTime: string | null | undefined): Date | undefined => {
  if (!isoDateTime) return undefined;
  return startOfDay(parseISO(getDateOnlyFromIsoDateTime(isoDateTime)));
};

export const areDatesEqual = (firstDate: string | null | undefined, secondDate: string | null | undefined): boolean => {
  if (!firstDate && !secondDate) return true;
  if (!firstDate || !secondDate) return false;
  return isSameDay(new Date(firstDate), new Date(secondDate));
};

export const formatDateForDB = (date: string | Date) => format(new Date(date), "yyyy-MM-dd");

export const formatNullableDateForDB = (date: Date | string | null | undefined) =>
  date ? formatDateForDB(date) : null;

export const formatDurationInHoursMinutes = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const minutesLeft = minutes % 60;
  return `${hours}h ${minutesLeft.toString().padStart(2, "0")}min`;
};

export const formatMinutesToShortHours = (minutes: number) => {
  if (minutes === 0) return "0h";
  const formatted = formatDurationInHoursMinutes(minutes);
  return formatted.replace(" ", "").replace("00min", "").replace("min", "").trim();
};

export const splitDurationInHoursAndMinutes = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const minutesLeft = minutes % 60;
  return { hours, minutes: minutesLeft };
};

/**
 * Monday of an ISO 8601 week, at local midnight.
 */
export const getStartOfWeek = ({ weekNumber, year }: { weekNumber: number; year: number }) => {
  // January 4th always falls in ISO week 1
  const dayInFirstWeek = new Date(year, 0, 4);
  if (weekNumber < 1 || weekNumber > getISOWeeksInYear(dayInFirstWeek)) {
    throw new Error(`Week ${weekNumber} does not exist in ISO week-year ${year}`);
  }
  return startOfISOWeek(setISOWeek(dayInFirstWeek, weekNumber));
};

export const getEndOfWeek = ({ weekNumber, year }: { weekNumber: number; year: number }) =>
  endOfISOWeek(getStartOfWeek({ weekNumber, year }));

export const getMonday = (date: Date) => {
  const formatedDate = new Date(date);
  const day = date.getDay();
  const diff = formatedDate.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(formatedDate.setDate(diff));
};

export const getDatesInWeek = ({ weekNumber, year }: { weekNumber: number; year: number }) => {
  const startOfWeek = getStartOfWeek({ weekNumber, year });
  const dates: Date[] = [];

  for (let i = 0; i < 7; i++) {
    dates.push(new Date(startOfWeek.getFullYear(), startOfWeek.getMonth(), startOfWeek.getDate() + i));
  }
  return dates;
};

export const sortDatesFromRecentToOld = (dates: Date[] | string[]) => {
  return dates.sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
};

export const sortDatesFromOldToRecent = (dates: Date[] | string[]) => {
  return dates.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
};

/**
 * ISO 8601 week and week-year of a date: 2027-01-01 is in week 53 of 2026.
 * Matches `timesheet_week.week_number` and `timesheet_week.year`, computed in SQL from the week's Monday.
 */
export const getWeekAndYear = (date: Date): { weekNumber: number; year: number } => ({
  weekNumber: getISOWeek(date),
  year: getISOWeekYear(date),
});

export const getMonthBasedOnSwitchDate = (currentDate: Date = new Date(), switchDate: Date) => {
  const startOfMonth = new Date(Date.UTC(currentDate.getFullYear(), currentDate.getMonth(), 1));
  const switchDateUTC = new Date(Date.UTC(switchDate.getFullYear(), switchDate.getMonth(), switchDate.getDate()));

  if (currentDate < switchDateUTC) {
    const previousMonth = new Date(Date.UTC(startOfMonth.getFullYear(), startOfMonth.getMonth() - 1, 1));
    return previousMonth;
  }

  return startOfMonth;
};

interface DateRange {
  start_at: Date | string | null;
  end_at: Date | string | null;
}

const formatYearMonth = (input: string | Date) => {
  const date = new Date(input);
  return date.getFullYear() * 100 + date.getMonth() + 1;
};

export const filterByHappeningOnDateMonth = <T extends DateRange>(items: T[], today?: Date) => {
  if (!today) today = new Date();
  const formattedToday = formatYearMonth(today);

  return items.filter((item) => {
    if (!item.start_at || !item.end_at) return false;

    const startDate = formatYearMonth(item.start_at);
    const endDate = formatYearMonth(item.end_at);

    return startDate <= formattedToday && formattedToday <= endDate;
  });
};

export const pickDayFromPreviousMonth = (date: Date) => {
  const previousMonth = new Date(date.getFullYear(), date.getMonth(), 0);
  return previousMonth;
};

export type MonthNumber = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11;

export function subBusinessDaysExcludingFrenchHolidays(date: Date, businessDays: number): Date {
  const { isPublicHoliday } = useHolidays("FR");
  let cursor = date;
  let remaining = businessDays;
  while (remaining > 0) {
    cursor = subDays(cursor, 1);
    if (!isWeekend(cursor) && !isPublicHoliday(cursor)) {
      remaining -= 1;
    }
  }
  return cursor;
}

export type MonthYear = {
  month: number;
  year: number;
};

/**
 * ISO 8601 weeks having at least one day in the month of the date, in chronological order.
 */
export function getWeeksInMonth(date: Date): { weekNumber: number; year: number }[] {
  const mondays = eachWeekOfInterval({ start: startOfMonth(date), end: endOfMonth(date) }, { weekStartsOn: 1 });
  return mondays.map(getWeekAndYear);
}

export function getWeeksInCurrentMonth(date: Date): number[] {
  return getWeeksInMonth(date).map(({ weekNumber }) => weekNumber);
}

export function getWeekDaysFromDateAndMonth(date: Date, month: Month) {
  const start = startOfWeek(date, { weekStartsOn: 1 });
  const end = endOfWeek(date, { weekStartsOn: 1 });

  const dates = eachDayOfInterval({ start, end });

  return dates.filter((d) => format(d, "MMMM").toLowerCase() === month.toLowerCase());
}

export function getWeeksBetweenDates(
  startDate: Date,
  endDate: Date,
  since?: string
): { weekNumber: number; year: number }[] {
  const today = new Date();
  const current = since && new Date(since) > startDate ? new Date(since) : new Date(startDate);

  current.setHours(0, 0, 0, 0); // Normalize current date to the Monday of that week
  const day = current.getDay() || 7;
  current.setDate(current.getDate() - (day - 1));

  const weeks: { weekNumber: number; year: number }[] = [];

  while (current <= endDate && current <= today) {
    const { weekNumber, year } = getWeekAndYear(current);
    weeks.push({ weekNumber, year });

    current.setDate(current.getDate() + 7); // Move to next week
  }

  return weeks;
}

/**
 * Checks if a string is a valid ISO 8601 date.
 * Uses strict parsing — only accepts properly formatted dates.
 *
 * @param value - The string to validate
 * @returns true if the string is a valid ISO date, false otherwise
 *
 * @example
 * isValidDate("2024-01-15")     // true
 * isValidDate("2024-01-15T10:30:00Z") // true
 * isValidDate("")               // false
 * isValidDate("not-a-date")     // false
 * isValidDate("2024-13-45")     // false
 */
export function isValidDate(value: string): boolean {
  if (!value || value.trim() === "") return false;
  const parsed = parseISO(value);
  return isValid(parsed);
}

export type PayPeriod = {
  month: Month;
  year: number;
  monthStart: Date;
  monthEnd: Date;
};

/**
 * Calculates the current pay period based on the pay block date.
 * - On/before the pay block date: Returns previous month (PLD processing previous month's pay)
 * - After the pay block date: Returns current month (PLD processing current month's pay)
 * Returns all needed values including monthStart and monthEnd as Date objects.
 *
 * @param options - Configuration options
 * @param options.payBlockDate - Day of month on/before which the previous month stays open (default: {@link PAY_BLOCK_DATE})
 * @param options.referenceDate - The date to calculate from (default: current date)
 * @returns Object containing month, year, monthStart, and monthEnd
 */
export function getCurrentPayPeriod({
  payBlockDate = PAY_BLOCK_DATE,
  referenceDate,
}: {
  payBlockDate?: number;
  referenceDate?: Date;
}): PayPeriod {
  const now = referenceDate ?? new Date();

  // Normalize payBlockDate: clamp to valid range for the reference month
  // Get the last day of the reference month (day 0 of next month)
  const daysInMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0)).getUTCDate();

  // Clamp payBlockDate to [1, daysInMonth]
  const normalizedPayBlockDate = Math.max(1, Math.min(payBlockDate, daysInMonth));

  const payBlockDateObj = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), normalizedPayBlockDate));
  const nowAsDay = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));

  const isAfterPayBlockDate = payBlockDateObj < nowAsDay;

  let monthStart: Date;
  let month: Month;
  let year: number;

  if (isAfterPayBlockDate) {
    // After the pay block date: Current month
    monthStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    // Convert 0-based JS Date month to 1-based month number for the map
    const monthValue = now.getUTCMonth() + 1;
    const mappedMonth = numberToMonthMap[monthValue];
    month = mappedMonth;
    year = monthStart.getUTCFullYear();
  } else {
    // On/before the pay block date: Previous month
    monthStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
    // Convert 0-based JS Date month to 1-based month number for the map
    const monthValue = monthStart.getUTCMonth() + 1;
    const mappedMonth = numberToMonthMap[monthValue];
    month = mappedMonth;
    year = monthStart.getUTCFullYear();
  }

  const monthEnd = endOfMonth(monthStart);

  return {
    month,
    year,
    monthStart,
    monthEnd,
  };
}
